import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../services/notificationService';
import { logout } from '../auth/authSlice';

export const fetchUnreadCount = createAsyncThunk(
  'notifications/fetchUnreadCount',
  async (_, { rejectWithValue }) => {
    try {
      return await getUnreadNotificationCount();
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to load unread count'
      );
    }
  }
);

export const fetchNotifications = createAsyncThunk(
  'notifications/fetchNotifications',
  async (params = {}, { rejectWithValue }) => {
    try {
      return await listNotifications({ per_page: 20, ...params });
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to load notifications'
      );
    }
  }
);

export const markOneRead = createAsyncThunk(
  'notifications/markOneRead',
  async (id, { rejectWithValue }) => {
    try {
      await markNotificationRead(id);
      return id;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to mark notification read'
      );
    }
  }
);

export const markAllRead = createAsyncThunk(
  'notifications/markAllRead',
  async (_, { rejectWithValue }) => {
    try {
      await markAllNotificationsRead();
      return true;
    } catch (error) {
      return rejectWithValue(
        error?.response?.data?.message || error?.message || 'Failed to mark all read'
      );
    }
  }
);

const initialState = {
  items: [],
  unreadCount: 0,
  listStatus: 'idle',
  listError: null,
  unreadStatus: 'idle',
};

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotificationsError(state) {
      state.listError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUnreadCount.pending, (state) => {
        state.unreadStatus = 'loading';
      })
      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadStatus = 'succeeded';
        state.unreadCount = Number(action.payload) || 0;
      })
      .addCase(fetchUnreadCount.rejected, (state) => {
        state.unreadStatus = 'failed';
      })
      .addCase(fetchNotifications.pending, (state) => {
        state.listStatus = 'loading';
        state.listError = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.listStatus = 'succeeded';
        state.items = action.payload?.items || [];
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.listStatus = 'failed';
        state.listError = action.payload || 'Failed to load notifications';
      })
      .addCase(markOneRead.fulfilled, (state, action) => {
        const id = action.payload;
        const item = state.items.find((n) => String(n.id) === String(id));
        if (item && item.isUnread) {
          item.isUnread = false;
          item.readAt = new Date().toISOString();
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        } else if (state.unreadCount > 0) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
      })
      .addCase(markAllRead.fulfilled, (state) => {
        state.items = state.items.map((n) => ({
          ...n,
          isUnread: false,
          readAt: n.readAt || new Date().toISOString(),
        }));
        state.unreadCount = 0;
      })
      .addCase(logout.fulfilled, () => initialState)
      .addCase(logout.rejected, () => initialState);
  },
});

export const { clearNotificationsError } = notificationsSlice.actions;
export default notificationsSlice.reducer;
